import React from "react";
import moment from "moment";

import ProfileIcon from "./icons/profile.svg";
import SecurityIcon from "./icons/security.svg";
import NotificationsIcon from "./icons/notifications.svg";
import PoliciesIcon from "./icons/policies.svg";
import ApiIcon from "./icons/api.svg";
import PromotionsIcon from "./icons/promotions.svg";
import { SectionProps } from "./components/Section";
import { IState } from "./features/profileAccountHook";
import { BFID_URL } from "@bundles/consts";
import defaultPhotoUrl from "@images/no_photo_male_big.png";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";

countries.registerLocale(en);

export const tabs = [
  {
    id: "profile",
    label: "Profile",
    icon: <ProfileIcon />,
  },
  {
    id: "security",
    label: "Password & Security",
    icon: <SecurityIcon />,
  },
  {
    id: "newsletter",
    label: "Newsletter",
    icon: <NotificationsIcon />,
  },
  {
    id: "agreementAndTerms",
    label: "Agreements & Terms",
    icon: <PoliciesIcon />,
  },
  {
    id: "api",
    label: "API management",
    icon: <ApiIcon />,
  },
  {
    id: "promotions",
    label: "Promotions",
    icon: <PromotionsIcon />,
  },
];

export const nameVisibilityOptions = [
  { value: "real_name", label: "Real name" },
  { value: "platform_name", label: "Platform name" },
  { value: "first_name", label: "First name" },
  {
    value: "first_letter_last_name",
    label: "First letter of first name + last name",
  },
];

export const editFieldModalParams = {
  platform_name: {
    title: "Choose platform name",
    description:
      "Based on your Name Visibility setting, this can be the nickname that will be shown on our public forum, investor zone and secondary market",
    fieldName: "platform_name",
    fieldLabel: "Platform name",
  },
  description: {
    title: "Description",
    fieldName: "description",
  },
  twitter_page: {
    title: "Twitter",
    fieldName: "twitter_page",
  },
  facebook_page: {
    title: "Facebook",
    fieldName: "facebook_page",
  },
  linkedin_page: {
    title: "LinkedIn",
    fieldName: "linkedin_page",
  },
  name_visibility: {
    title: "Name visibility",
    description:
      "Choose how your name appears on our public forum, investor zone, and secondary market.",
    fieldName: "name_visibility",
    fieldType: "select",
    fieldOptions: nameVisibilityOptions,
  },
};

export const getProfileSections = (
  state?: IState,
  setActiveTab?: (tab?: string) => void,
  onEditBtnClick?: (fieldName: string, initialValue?: string) => void,
  handleOpenCloseAccountModal?: () => void,
  is2FAEnabled?: boolean
): SectionProps[] => {
  const { profileData } = state;

  return [
    {
      title: "Account information",
      fields: [
        {
          label: "Email",
          value: profileData?.verification?.email,
          hasEditButton: true,
          editBtnText: "Change email",
          onEditBtnClick: is2FAEnabled
            ? () => onEditBtnClick("email")
            : undefined,
          tooltipText: "Enable 2FA to change your email",
        },
        {
          label: "Password",
          value: "",
          isSecret: true,
          customAction: (
            <p className="text-grey-lightest text-sm">
              Edit your password in{" "}
              <a
                onClick={
                  setActiveTab ? () => setActiveTab("security") : undefined
                }
                className="font-normal underline"
              >
                Password & Security
              </a>
            </p>
          ),
        },
      ],
    },
    {
      title: "Personal information",
      displayUpdateBtn: true,
      updateActionLink: BFID_URL,
      fields: [
        { label: "Full name", value: profileData?.verification?.full_name },
        {
          label: "Date of birth",
          value: moment(profileData?.verification?.date_of_birth).format(
            "DD MMM YYYY"
          ),
        },
      ],
    },
    {
      title: "Address details",
      displayUpdateBtn: true,
      updateActionLink: BFID_URL,
      fields: [
        {
          label: "Address",
          value: `${profileData?.verification?.address?.line_1 ?? ""} ${
            profileData?.verification?.address?.line_2 ?? ""
          }`,
        },
        { label: "City", value: profileData?.verification?.address?.city },
        { label: "State", value: profileData?.verification?.address?.state },
        {
          label: "Postal code",
          value: profileData?.verification?.address?.postcode,
        },
        {
          label: "Country",
          value: countries.getName(
            profileData?.verification?.address?.country,
            "en"
          ),
        },
      ],
    },
    {
      title: "Account details",
      additionalFieldsSpace: true,
      fields: [
        {
          label: "Profile picture",
          value: profileData?.profile.image_url || defaultPhotoUrl,
          isImage: true,
          hasEditButton: true,
          onEditBtnClick: () => onEditBtnClick("profile_picture"),
        },
        {
          label: "Platform name",
          value: profileData?.profile.platform_name || "None",
          hasEditButton: true,
          onEditBtnClick: () =>
            onEditBtnClick("platform_name", profileData?.profile.platform_name),
        },
        {
          label: "Name visibility",
          value:
            nameVisibilityOptions.find(
              (opt) => opt.value === profileData?.profile.name_visibility
            )?.label || "None",
          hasEditButton: true,
          onEditBtnClick: () =>
            onEditBtnClick(
              "name_visibility",
              profileData?.profile.name_visibility
            ),
        },
        {
          label: "Description",
          value: profileData?.profile?.description || "None",
          hasEditButton: true,
          fullValue: true,
          onEditBtnClick: () =>
            onEditBtnClick("description", profileData?.profile?.description),
        },
      ],
    },
    {
      title: "Social media accounts",
      additionalFieldsSpace: true,
      fields: [
        {
          label: "Twitter",
          value: profileData?.profile?.twitter_page || "None",
          hasEditButton: true,
          onEditBtnClick: () =>
            onEditBtnClick("twitter_page", profileData?.profile?.twitter_page),
        },
        {
          label: "Facebook",
          value: profileData?.profile?.facebook_page || "None",
          hasEditButton: true,
          onEditBtnClick: () =>
            onEditBtnClick(
              "facebook_page",
              profileData?.profile?.facebook_page
            ),
        },
        {
          label: "LinkedIn",
          value: profileData?.profile?.linkedin_page || "None",
          hasEditButton: true,
          onEditBtnClick: () =>
            onEditBtnClick(
              "linkedin_page",
              profileData?.profile?.linkedin_page
            ),
        },
      ],
    },
    {
      title: "BNK of the future account",
      fields: [
        {
          label: "Account type",
          value: state?.isOrganization ? "Organization" : "Individual",
        },
        {
          label: "Account creation date",
          value: moment(state?.created_at).format("DD MMM YYYY"),
          hasEditButton: state?.canCloseAccount,
          onEditBtnClick: state?.canCloseAccount
            ? handleOpenCloseAccountModal
            : undefined,
          customEditBtn: state?.canCloseAccount
            ? {
                label: "Close my Bnk To The Future account",
              }
            : undefined,
        },
      ],
    },
  ];
};

export const termsAndConditionsTypeLabels: { [key: string]: string } = {
  global: "BF Platform Operator",
  portfolio_builder: "Portfolio Builder Terms",
  securities: "BF Securities Broker",
  staking: "Staking Terms",
  virtual_assets: "BF Virtual Asset Broker",
};

export const getSeurityActivitiesDataSource = (data: Array<any>) =>
  data.map((item, index) => ({
    key: String(index),
    action: item?.action?.replace("Two fa", "2FA"),
    ip: item?.ip,
    country: item?.country,
    created_at: moment(item?.created_at).format("MMM D, YYYY"),
  }));

export const securityActivitiesColumns = [
  {
    title: "ACTION",
    dataIndex: "action",
    key: "action",
    sorter: (a, b) => a.action.localeCompare(b.action),
  },
  {
    title: "IP ADDRESS",
    dataIndex: "ip",
    key: "ip",
    sorter: (a, b) => a.ip.localeCompare(b.ip),
  },
  {
    title: "LOCATION",
    dataIndex: "country",
    key: "country",
    sorter: (a, b) => a.country.localeCompare(b.country),
  },
  {
    title: "DATE",
    dataIndex: "created_at",
    key: "created_at",
    sorter: (a, b) =>
      moment(a.created_at, "MMM D, YYYY").unix() -
      moment(b.created_at, "MMM D, YYYY").unix(),
  },
];

export const mobileSecurityActivitiesColumns = [
  {
    title: "ACTION",
    dataIndex: "action",
    key: "action",
    sorter: (a, b) => a.action.localeCompare(b.action),
  },
  {
    title: "DATE",
    dataIndex: "created_at",
    key: "created_at",
    sorter: (a, b) =>
      moment(a.created_at, "MMM D, YYYY").unix() -
      moment(b.created_at, "MMM D, YYYY").unix(),
    align: "right",
  },
];
